import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useNavigate } from "react-router-dom";
import Check from "@mui/icons-material/Check";

const INK = "#1d2340";
const PAPER = "#f8f5ee";
const LINE = "#e7e1d3";
const PEN = "#d9412b";
const MARKER = "#ffe58a";
const MUTED = "#6f6a5c";

const HERO_STATS = [
    { value: "1,200+", label: "누적 수강생" },
    { value: "40장", label: "1문제당 피드백" },
    { value: "무제한", label: "리라이팅 첨삭" },
    { value: "22명", label: "SKY 출신 강사" },
];

const PILLARS = [
    {
        title: "4단계 첨삭",
        text: "독해력 → 사고력 → 표현력 → 밑줄. 채점 기준 순서대로 한 단계씩 교정합니다.",
    },
    {
        title: "무제한 리라이팅",
        text: "합격 수준에 도달할 때까지 다시 쓰고 다시 첨삭받습니다. 추가 비용은 없습니다.",
    },
    {
        title: "수능 국어 연계",
        text: "지문 구조 분석은 곧 수능 비문학 독해. 논술 공부가 국어 실력으로 이어집니다.",
    },
];

const PAINS = [
    {
        title: "10분 첨삭의 한계",
        quote: "조교가 원고지만 훑어보고 끝나요. 뭘 고쳐야 할지 모르겠어요.",
    },
    {
        title: "한 번 쓰고 끝",
        quote: "다시 써서 보여드리고 싶은데, 추가 첨삭은 6~7만원이래요.",
    },
    {
        title: "채점 기준을 모름",
        quote: "배경지식은 외웠는데, 왜 점수가 안 나오는지 모르겠어요.",
    },
    {
        title: "비용 대비 성과",
        quote: "월 10만원 넘게 내는데 개인 첨삭은 수업 중에 잠깐이 전부예요.",
    },
];

const TARGETS = [
    "글은 고쳤는데 점수가 그대로인 학생",
    "논술과 수능 국어 독해력을 동시에 올리고 싶은 학생",
    "지방에서도 SKY 출신 선생님께 첨삭받고 싶은 학생",
];

const STEP_TABS = [
    {
        tab: "STEP 1",
        title: "독해력 첨삭",
        desc: "교수님의 채점 기준인 ‘독해력’을 직접 교정합니다.",
        points: ["지문 이해력 분석", "논제 핵심 파악", "문단별 요지 도출"],
        pill: "수능 국어 독해력 동시 향상",
    },
    {
        tab: "STEP 2",
        title: "사고력 첨삭",
        desc: "단순 서술이 아닌 ‘사유하는 글쓰기’로 경쟁자와 차별화합니다.",
        points: ["논리적 전개 구조", "근거·예시 강화", "비판적 시각 훈련"],
        pill: "채점기준 90% 독해·사고력",
    },
    {
        tab: "STEP 3",
        title: "표현력 첨삭",
        desc: "교수님 눈에 띄는 차별화된 표현 기술을 익힙니다.",
        points: ["문장 명확성", "학술적 표현", "설득력 강화"],
        pill: "서술형 문항 완성도 향상",
    },
    {
        tab: "STEP 4",
        title: "밑줄 첨삭",
        desc: "문장 하나하나, 어휘 하나하나까지 정밀 교정합니다.",
        points: ["세부 문장 구조 교정", "반복 어휘 최소화", "문체 통일"],
        pill: "문장·어휘 단위 정밀 교정",
    },
    {
        tab: "FINAL",
        title: "무제한 리라이팅",
        desc: "합격 수준에 도달할 때까지 다시 쓰고, 다시 첨삭받습니다.",
        points: ["리라이팅은 의무 숙제", "원하는 만큼 첨삭 요청", "추가 비용 없음"],
        pill: "합격할 때까지 무료 무제한",
    },
];

const TIMETABLE = [
    { time: "1교시", title: "복습 테스트 & 비문학 훈련" },
    { time: "2교시", title: "차별화된 개념 & 기술 습득" },
    { time: "3교시", title: "학생 스스로 실전 문제풀이" },
    { time: "4교시", title: "일관된 독해방법 해설강의" },
    { time: "수업 후", title: "1인당 1시간 이상 개인 첨삭" },
];

const COMPARE_ROWS = [
    { label: "첨삭 분량", other: "원고지 10분", pass: "1문제당 40페이지 정밀 피드백" },
    { label: "첨삭 단계", other: "단순 문법·표현 수정", pass: "독해 → 사고력 → 표현력 → 밑줄" },
    { label: "첨삭 횟수", other: "1회 (추가 시 6~7만원)", pass: "합격할 때까지 무제한 무료" },
    { label: "집중 피드백", other: "수업 중 짧게", pass: "별도 1시간 이상 집중 첨삭" },
    { label: "월 비용", other: "10만원 이상", pass: "월 50,000원부터" },
];

const PLANS = [
    {
        name: "AI 첨삭권",
        caption: "혼자서도 꾸준히",
        price: "50,000",
        features: ["무제한 AI 4단계 첨삭", "실시간 학습 리포트", "강의 수강"],
        cta: "바로 시작하기",
        primary: true,
    },
    {
        name: "온라인 단체 수업",
        caption: "소수 정예로 함께",
        price: "400,000",
        features: ["소수 정예 그룹 수업", "4단계 그룹 첨삭", "무제한 리라이팅", "경쟁자 분석 수업"],
        cta: "수강 문의",
    },
    {
        name: "온라인 1:1 과외",
        caption: "나에게만 맞춘",
        price: "500,000",
        features: ["1:1 맞춤 실시간 수업", "4단계 개인 집중 첨삭", "무제한 리라이팅", "개인화 커리큘럼 설계"],
        cta: "수강 문의",
    },
];

const UNIVERSITIES = ["연세대", "서강대", "중앙대", "한양대", "성균관대", "경희대", "한국외대", "광운대"];

const REVIEWS = [
    {
        message: "저 수능 4등급이라 인서울은 상상도 못했는데 연세대 합격했어요, 덕분에 인생이 바뀌었어요",
        outcome: "연세대학교 논술전형",
    },
    {
        message: "샘 저 장학생으로 합격했어요!!!!!!!!!!",
        outcome: "논술전형 합격 · 장학생",
    },
    {
        message: "선생님~~~~~ 저 합격했어용😭😭😭 발표 보고 혼자 한참 울었어요..",
        outcome: "중앙대학교 논술전형",
    },
    {
        message: "선생님!!!!!!!! 저 붙었어요붙었어요붙었어요 ㅠㅠㅠㅠㅠㅠ 외대중앙대기술때문에 붙은거같아요",
        outcome: "한국외대 · 중앙대 복수합격",
    },
];

const PROCESS = [
    { title: "회원가입", text: "이메일 또는 Google 계정으로 가입해요." },
    { title: "무료 첨삭 체험", text: "4단계 첨삭을 직접 받아보고 차이를 확인해요." },
    { title: "수강권 선택", text: "AI 첨삭권, 단체 수업, 1:1 과외 중에서 골라요." },
    { title: "수업 시작", text: "강의 수강과 첨삭, 리라이팅을 바로 시작해요." },
];

const FAQS = [
    {
        q: "수강료는 얼마인가요?",
        a: "AI 첨삭권은 월 50,000원, 온라인 단체 수업은 월 400,000원, 온라인 1:1 과외는 월 500,000원입니다.",
    },
    {
        q: "리라이팅 첨삭은 몇 번까지 받을 수 있나요?",
        a: "횟수 제한이 없습니다. 합격 수준에 도달할 때까지 추가 비용 없이 원하는 만큼 첨삭을 요청할 수 있어요.",
    },
    {
        q: "AI 첨삭권과 수업 과정은 무엇이 다른가요?",
        a: "AI 첨삭권은 무제한 AI 4단계 첨삭, 실시간 학습 리포트, 강의 수강이 포함됩니다. 선생님 수기 첨삭과 개인화 리포트 PDF는 포함되지 않아요.",
    },
    {
        q: "논술 공부가 수능에도 도움이 되나요?",
        a: "2008년 이후 논술 채점기준은 수능 국어 독해력 중심입니다. 지문 구조 분석과 논리 구성 훈련이 수능 비문학 독해와 그대로 이어집니다.",
    },
    {
        q: "환불은 어떻게 되나요?",
        a: "결제일로부터 7일 이내 청약철회를 요청할 수 있습니다. 자세한 기준은 페이지 하단의 환불정책을 확인해 주세요.",
    },
];

const TRUST_ITEMS = [
    "정부 기술 특허 보유",
    "K-Startup 선정기업",
    "2년간 최저충족 학생 100% 논술합격",
    "SKY 출신 전문강사 22명",
    "1,200명+ 수강생",
    "한양대 대학원 최연소 합격",
    "지방학생도 SKY 출신 선생님께 첨삭",
    "서울 전체 학원 직접 수강 분석",
];

const Intro = () => {
    const navigate = useNavigate();
    const [showFloating, setShowFloating] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [openFaq, setOpenFaq] = useState(0);

    useEffect(() => {
        const onScroll = () => {
            const footer = document.querySelector('footer[role="contentinfo"]');
            const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
            setShowFloating(window.scrollY > 560 && !footerVisible);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleInquiry = () => {
        window.location.href = "https://pf.kakao.com/_saaTG";
    };

    const handleStart = () => {
        navigate("/register");
    };

    const step = STEP_TABS[activeStep];

    return (
        <Page>
            <Hero>
                <HeroInner>
                    <HeroCopy>
                        <Kicker>전국 최초 · 정부 기술 특허 · AI 4단계 첨삭</Kicker>
                        <HeroTitle>
                            논술은 첨삭이 아니라
                            <br />
                            <Marker>사고력 코칭</Marker>입니다
                        </HeroTitle>
                        <HeroLead>
                            원고지 10분 첨삭으로는 독해력과 사고력이 바뀌지 않습니다. 패스로는 1문제당 40페이지,
                            합격할 때까지 다시 쓰게 합니다.
                        </HeroLead>
                        <ButtonRow>
                            <InkButton type="button" onClick={handleStart}>
                                4단계 첨삭 무료 체험
                            </InkButton>
                            <YellowButton type="button" onClick={handleInquiry}>
                                카카오톡 상담
                            </YellowButton>
                        </ButtonRow>
                        <StatRow>
                            {HERO_STATS.map((stat) => (
                                <Stat key={stat.label}>
                                    <strong>{stat.value}</strong>
                                    <span>{stat.label}</span>
                                </Stat>
                            ))}
                        </StatRow>
                    </HeroCopy>

                    <Sheet aria-hidden>
                        <SheetHead>
                            <span>논술 답안지</span>
                            <SheetTag>STEP 2 · 사고력 첨삭 중</SheetTag>
                        </SheetHead>
                        <SheetBody>
                            <SheetLine $w={94} />
                            <SheetLine $w={88} $strike />
                            <SheetLine $w={72} />
                            <SheetLine $w={90} $circle />
                            <SheetLine $w={60} />
                            <SheetLine $w={84} $underline />
                            <SheetLine $w={78} />
                            <SheetLine $w={66} />
                        </SheetBody>
                        <MarginNote style={{ top: "92px" }}>논제 이탈?</MarginNote>
                        <MarginNote style={{ top: "168px" }}>근거 부족</MarginNote>
                        <MarginNote style={{ top: "236px" }}>표현 다듬기</MarginNote>
                        <SheetFoot>
                            <span>피드백 12 / 40p</span>
                            <span>리라이팅 2회차</span>
                        </SheetFoot>
                    </Sheet>
                </HeroInner>
            </Hero>

            <Section id="intro" $bg="#fff">
                <Inner>
                    <SectionHead>
                        <Eyebrow>패스로는 무엇이 다른가요</Eyebrow>
                        <SectionTitle>
                            채점 기준대로 고치고,
                            <br />
                            합격할 때까지 다시 씁니다
                        </SectionTitle>
                    </SectionHead>
                    <PillarGrid>
                        {PILLARS.map((pillar, index) => (
                            <Pillar key={pillar.title}>
                                <PillarNum>{String(index + 1).padStart(2, "0")}</PillarNum>
                                <h3>{pillar.title}</h3>
                                <p>{pillar.text}</p>
                            </Pillar>
                        ))}
                    </PillarGrid>
                </Inner>
            </Section>

            <Section>
                <Inner>
                    <SplitRow>
                        <SplitAside>
                            <Eyebrow>이런 고민 있나요</Eyebrow>
                            <SectionTitle>
                                지금 다니는 학원,
                                <br />
                                이런 문제 없으신가요?
                            </SectionTitle>
                            <AsideNote>
                                문제는 글이 아니라 <Marker>독해와 사고</Marker>입니다. 패스로는 거기서부터 고칩니다.
                            </AsideNote>
                        </SplitAside>
                        <PainList>
                            {PAINS.map((pain) => (
                                <PainItem key={pain.title}>
                                    <PainTitle>{pain.title}</PainTitle>
                                    <p>“{pain.quote}”</p>
                                </PainItem>
                            ))}
                        </PainList>
                    </SplitRow>

                    <TargetBox>
                        <TargetTitle>이런 학생에게 추천해요</TargetTitle>
                        <TargetList>
                            {TARGETS.map((target) => (
                                <li key={target}>
                                    <PenCheck aria-hidden>✓</PenCheck>
                                    {target}
                                </li>
                            ))}
                        </TargetList>
                    </TargetBox>
                </Inner>
            </Section>

            <Section $bg="#fff">
                <Inner>
                    <SectionHead $center>
                        <Eyebrow>첨삭 비교</Eyebrow>
                        <SectionTitle>같은 답안, 다른 첨삭</SectionTitle>
                    </SectionHead>
                    <BeforeAfter>
                        <PaperCard>
                            <PaperLabel>대부분의 학원</PaperLabel>
                            <PaperTitle>원고지 10분 첨삭</PaperTitle>
                            <MiniSheet>
                                <SheetLine $w={92} />
                                <SheetLine $w={80} $strike />
                                <SheetLine $w={88} />
                                <SheetLine $w={70} />
                            </MiniSheet>
                            <PaperList>
                                <li>맞춤법 · 표현 위주 수정</li>
                                <li>1회 첨삭 후 종료</li>
                                <li>추가 첨삭은 6~7만원</li>
                            </PaperList>
                        </PaperCard>
                        <PaperCard $highlight>
                            <PaperLabel $highlight>PASSRO</PaperLabel>
                            <PaperTitle>4단계 심층 첨삭</PaperTitle>
                            <StepTrack>
                                {["독해", "사고", "표현", "밑줄"].map((name, index) => (
                                    <StepDot key={name} $done={index < 2} $current={index === 2}>
                                        <i>{index < 2 ? "✓" : index + 1}</i>
                                        {name}
                                    </StepDot>
                                ))}
                            </StepTrack>
                            <PaperList $highlight>
                                <li>1문제당 40페이지 피드백</li>
                                <li>합격할 때까지 무제한 리라이팅</li>
                                <li>수업 후 1인당 1시간 이상 개인 첨삭</li>
                            </PaperList>
                        </PaperCard>
                    </BeforeAfter>
                </Inner>
            </Section>

            <Section id="curriculum" $bg={INK} $dark>
                <Inner>
                    <SectionHead>
                        <Eyebrow $dark>커리큘럼</Eyebrow>
                        <SectionTitle $dark>한 문제를 다섯 번 다시 보는 구조</SectionTitle>
                    </SectionHead>

                    <CurriculumGrid>
                        <StepMenu role="tablist" aria-label="첨삭 단계">
                            {STEP_TABS.map((item, index) => (
                                <StepMenuItem
                                    key={item.tab}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeStep === index}
                                    $active={activeStep === index}
                                    onClick={() => setActiveStep(index)}
                                >
                                    <small>{item.tab}</small>
                                    {item.title}
                                </StepMenuItem>
                            ))}
                        </StepMenu>
                        <StepPanel role="tabpanel">
                            <small>{step.tab}</small>
                            <h3>{step.title}</h3>
                            <p>{step.desc}</p>
                            <ol>
                                {step.points.map((point) => (
                                    <li key={point}>{point}</li>
                                ))}
                            </ol>
                            <StepPill>{step.pill}</StepPill>
                        </StepPanel>
                    </CurriculumGrid>

                    <Timetable>
                        <TimetableHead>패스로 수업 시간표</TimetableHead>
                        {TIMETABLE.map((row) => (
                            <TimetableRow key={row.time}>
                                <span>{row.time}</span>
                                <p>{row.title}</p>
                            </TimetableRow>
                        ))}
                    </Timetable>
                </Inner>
            </Section>

            <Section id="pricing">
                <Inner>
                    <SectionHead $center>
                        <Eyebrow>수강료</Eyebrow>
                        <SectionTitle>학원비는 줄이고, 첨삭은 늘리고</SectionTitle>
                    </SectionHead>

                    <TableScroll role="region" aria-label="기존 학원과 패스로 비교">
                        <CompareTable>
                            <thead>
                                <tr>
                                    <th>비교 항목</th>
                                    <th>대부분의 논술학원</th>
                                    <th>패스로</th>
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARE_ROWS.map((row) => (
                                    <tr key={row.label}>
                                        <td>{row.label}</td>
                                        <td>{row.other}</td>
                                        <td>{row.pass}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </CompareTable>
                    </TableScroll>

                    <PlanGrid>
                        {PLANS.map((plan) => (
                            <PlanCard key={plan.name} $primary={plan.primary}>
                                {plan.primary && <PlanRibbon>처음이라면 추천</PlanRibbon>}
                                <PlanCaption>{plan.caption}</PlanCaption>
                                <PlanName>{plan.name}</PlanName>
                                <PlanPrice>
                                    <strong>{plan.price}</strong>원 / 월
                                </PlanPrice>
                                <PlanList>
                                    {plan.features.map((feature) => (
                                        <li key={feature}>
                                            <Check sx={{ fontSize: 16 }} />
                                            {feature}
                                        </li>
                                    ))}
                                </PlanList>
                                {plan.primary ? (
                                    <InkButton type="button" onClick={handleStart} $block>
                                        {plan.cta}
                                    </InkButton>
                                ) : (
                                    <OutlineButton type="button" onClick={handleInquiry} $block>
                                        {plan.cta}
                                    </OutlineButton>
                                )}
                            </PlanCard>
                        ))}
                    </PlanGrid>
                    <FinePrint>리라이팅은 의무 숙제예요. 원하는 만큼 다시 쓰고, 원하는 만큼 첨삭받을 수 있어요.</FinePrint>
                </Inner>
            </Section>

            <Section id="reviews" $bg="#fff">
                <Inner>
                    <SectionHead $center>
                        <Eyebrow>합격 후기</Eyebrow>
                        <SectionTitle>먼저 합격한 선배들이 보낸 메시지</SectionTitle>
                    </SectionHead>
                    <UniRow>
                        {UNIVERSITIES.map((name) => (
                            <span key={name}>{name}</span>
                        ))}
                    </UniRow>
                    <NoteGrid>
                        {REVIEWS.map((review, index) => (
                            <Note key={review.outcome} $tilt={index % 2 === 0 ? -1.2 : 1.2}>
                                <p>{review.message}</p>
                                <NoteOutcome>{review.outcome}</NoteOutcome>
                            </Note>
                        ))}
                    </NoteGrid>
                </Inner>
            </Section>

            <Section>
                <Inner>
                    <SectionHead $center>
                        <Eyebrow>수강 절차</Eyebrow>
                        <SectionTitle>수강은 이렇게 시작해요</SectionTitle>
                    </SectionHead>
                    <ProcessRow>
                        {PROCESS.map((item, index) => (
                            <ProcessItem key={item.title}>
                                <ProcessNum>{index + 1}</ProcessNum>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </ProcessItem>
                        ))}
                    </ProcessRow>
                </Inner>
            </Section>

            <Section id="faq" $bg="#fff">
                <Inner $narrow>
                    <SectionHead $center>
                        <Eyebrow>FAQ</Eyebrow>
                        <SectionTitle>자주 묻는 질문</SectionTitle>
                    </SectionHead>
                    <FaqList>
                        {FAQS.map((faq, index) => {
                            const open = openFaq === index;
                            return (
                                <FaqItem key={faq.q}>
                                    <FaqQuestion
                                        type="button"
                                        aria-expanded={open}
                                        onClick={() => setOpenFaq(open ? null : index)}
                                    >
                                        {faq.q}
                                        <FaqSign aria-hidden>{open ? "−" : "+"}</FaqSign>
                                    </FaqQuestion>
                                    {open && <FaqAnswer>{faq.a}</FaqAnswer>}
                                </FaqItem>
                            );
                        })}
                    </FaqList>
                </Inner>
            </Section>

            <Section $bg={INK} $dark>
                <Inner>
                    <ClosingGrid>
                        <div>
                            <SectionTitle $dark>
                                논술은 교정이 아니라
                                <br />
                                <Marker $dark>사고력 코칭</Marker>입니다
                            </SectionTitle>
                            <ClosingLead>소수 정예 모집 중이에요. 지금 4단계 첨삭을 무료로 받아보세요.</ClosingLead>
                            <ButtonRow>
                                <PenButton type="button" onClick={handleStart}>
                                    무료로 첨삭 받아보기
                                </PenButton>
                                <YellowButton type="button" onClick={handleInquiry}>
                                    카카오톡 상담
                                </YellowButton>
                            </ButtonRow>
                        </div>
                        <TrustList>
                            {TRUST_ITEMS.map((item) => (
                                <li key={item}>
                                    <PenCheck aria-hidden $dark>
                                        ✓
                                    </PenCheck>
                                    {item}
                                </li>
                            ))}
                        </TrustList>
                    </ClosingGrid>
                </Inner>
            </Section>

            <Floating $visible={showFloating} aria-hidden={!showFloating}>
                <FloatingCopy>
                    <strong>4단계 첨삭, 먼저 받아보세요</strong>
                    <span>가입하면 바로 무료 체험</span>
                </FloatingCopy>
                <FloatingActions>
                    <FloatingGhost type="button" onClick={handleInquiry}>
                        상담
                    </FloatingGhost>
                    <PenButton type="button" onClick={handleStart}>
                        무료 체험
                    </PenButton>
                </FloatingActions>
            </Floating>
        </Page>
    );
};

export default Intro;

const Page = styled.div`
    width: 100%;
    min-height: 100vh;
    overflow-x: clip;
    background: ${PAPER};
    color: ${INK};
    font-family: "Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, system-ui,
        "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif;
    font-weight: 400;
    letter-spacing: -0.02em;
    word-break: keep-all;

    section[id] {
        scroll-margin-top: 72px;

        @media (max-width: 860px) {
            scroll-margin-top: 112px;
        }
    }
`;

const Marker = styled.span`
    background: linear-gradient(transparent 58%, ${({ $dark }) => ($dark ? "rgba(255, 229, 138, 0.35)" : MARKER)} 58%);
`;

const buttonBase = css`
    padding: 15px 22px;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.02em;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    ${({ $block }) => $block && "width: 100%;"}
`;

const InkButton = styled.button`
    ${buttonBase}
    color: #fff;
    background: ${INK};
    border: 1px solid ${INK};

    &:hover {
        background: #2c3561;
    }
`;

const PenButton = styled.button`
    ${buttonBase}
    color: #fff;
    background: ${PEN};
    border: 1px solid ${PEN};

    &:hover {
        background: #c13520;
    }
`;

const YellowButton = styled.button`
    ${buttonBase}
    color: ${INK};
    background: ${MARKER};
    border: 1px solid ${MARKER};

    &:hover {
        background: #ffd84a;
    }
`;

const OutlineButton = styled.button`
    ${buttonBase}
    color: ${({ $dark }) => ($dark ? "#fff" : INK)};
    background: transparent;
    border: 1px solid ${({ $dark }) => ($dark ? "rgba(255, 255, 255, 0.4)" : INK)};

    &:hover {
        background: ${({ $dark }) => ($dark ? "rgba(255, 255, 255, 0.08)" : "rgba(29, 35, 64, 0.06)")};
    }
`;

const ButtonRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Hero = styled.section`
    padding: 64px 20px 72px;
    border-bottom: 1px solid ${LINE};

    @media (max-width: 860px) {
        padding: 40px 20px 56px;
    }
`;

const HeroInner = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 56px;
    align-items: center;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
        gap: 40px;
    }
`;

const HeroCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 22px;
`;

const Kicker = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${PEN};
`;

const HeroTitle = styled.h1`
    margin: 0;
    font-size: clamp(2rem, 4.2vw, 2.85rem);
    font-weight: 700;
    line-height: 1.32;
    letter-spacing: -0.032em;
`;

const HeroLead = styled.p`
    margin: 0;
    max-width: 500px;
    font-size: 16px;
    line-height: 1.75;
    color: ${MUTED};
`;

const StatRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 10px;
    padding-top: 22px;
    border-top: 1px solid ${LINE};

    @media (max-width: 480px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        row-gap: 16px;
    }
`;

const Stat = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
        font-size: 22px;
        font-weight: 700;
        letter-spacing: -0.03em;
    }

    span {
        font-size: 12px;
        color: ${MUTED};
    }
`;

const Sheet = styled.div`
    position: relative;
    padding: 22px 96px 18px 26px;
    background:
        repeating-linear-gradient(transparent 0 33px, #efe6d2 33px 34px),
        #fffdf7;
    border: 1px solid ${LINE};
    border-radius: 4px;
    box-shadow:
        8px 8px 0 #ece5d4,
        0 18px 40px rgba(29, 35, 64, 0.08);
    transform: rotate(1.2deg);

    &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        right: 84px;
        width: 1px;
        background: #f2b8ad;
    }

    @media (max-width: 480px) {
        padding-right: 84px;
        transform: none;
    }
`;

const SheetHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: 34px;
    margin-right: -70px;
    font-size: 15px;
    font-weight: 700;
`;

const SheetTag = styled.span`
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background: ${INK};
    border-radius: 3px;
`;

const SheetBody = styled.div`
    display: flex;
    flex-direction: column;
`;

const MiniSheet = styled(SheetBody)`
    margin: 4px 0 18px;
    padding: 4px 14px;
    background:
        repeating-linear-gradient(transparent 0 33px, #efe6d2 33px 34px),
        #fffdf7;
    border: 1px solid ${LINE};
`;

const SheetLine = styled.div`
    position: relative;
    height: 34px;
    display: flex;
    align-items: center;

    &::before {
        content: "";
        width: ${({ $w }) => $w}%;
        height: 7px;
        border-radius: 4px;
        background: #d8d1bf;
    }

    ${({ $strike }) =>
        $strike &&
        css`
            &::after {
                content: "";
                position: absolute;
                left: 0;
                width: 46%;
                height: 2px;
                background: ${PEN};
                transform: rotate(-2deg);
            }
        `}

    ${({ $circle }) =>
        $circle &&
        css`
            &::after {
                content: "";
                position: absolute;
                left: 30%;
                width: 34%;
                height: 22px;
                border: 2px solid ${PEN};
                border-radius: 50%;
            }
        `}

    ${({ $underline }) =>
        $underline &&
        css`
            &::after {
                content: "";
                position: absolute;
                left: 8%;
                bottom: 6px;
                width: 60%;
                height: 2px;
                background: ${PEN};
            }
        `}
`;

const MarginNote = styled.span`
    position: absolute;
    right: 10px;
    width: 66px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    color: ${PEN};
    transform: rotate(-4deg);
`;

const SheetFoot = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 10px -70px 0 0;
    padding-top: 12px;
    font-size: 12px;
    font-weight: 700;
    color: ${MUTED};
    border-top: 1px dashed ${LINE};
`;

const Section = styled.section`
    padding: 88px 20px;
    background: ${({ $bg }) => $bg || PAPER};
    color: ${({ $dark }) => ($dark ? "#fff" : INK)};

    @media (max-width: 860px) {
        padding: 64px 20px;
    }
`;

const Inner = styled.div`
    max-width: ${({ $narrow }) => ($narrow ? "760px" : "1120px")};
    margin: 0 auto;
`;

const SectionHead = styled.div`
    margin-bottom: 40px;
    text-align: ${({ $center }) => ($center ? "center" : "left")};
`;

const Eyebrow = styled.p`
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${({ $dark }) => ($dark ? "#ff9a85" : PEN)};
`;

const SectionTitle = styled.h2`
    margin: 0;
    font-size: clamp(1.5rem, 3.2vw, 2.05rem);
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: -0.028em;
    color: ${({ $dark }) => ($dark ? "#fff" : INK)};
`;

const PillarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-top: 2px solid ${INK};

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

const Pillar = styled.div`
    padding: 28px 28px 8px 0;

    & + & {
        padding-left: 28px;
        border-left: 1px solid ${LINE};
    }

    h3 {
        margin: 14px 0 10px;
        font-size: 18px;
        font-weight: 600;
        letter-spacing: -0.024em;
    }

    p {
        margin: 0;
        font-size: 15px;
        line-height: 1.7;
        color: ${MUTED};
    }

    @media (max-width: 860px) {
        padding: 24px 0;

        & + & {
            padding-left: 0;
            border-left: none;
            border-top: 1px solid ${LINE};
        }
    }
`;

const PillarNum = styled.span`
    font-size: 34px;
    font-weight: 700;
    color: ${PEN};
`;

const SplitRow = styled.div`
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    gap: 48px;
    align-items: start;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
        gap: 28px;
    }
`;

const SplitAside = styled.div`
    position: sticky;
    top: 120px;

    @media (max-width: 860px) {
        position: static;
    }
`;

const AsideNote = styled.p`
    margin: 22px 0 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.7;
`;

const PainList = styled.div`
    display: grid;
    gap: 12px;
`;

const PainItem = styled.div`
    padding: 22px 24px;
    background: #fff;
    border: 1px solid ${LINE};
    border-left: 4px solid ${PEN};
    border-radius: 4px;

    p {
        margin: 8px 0 0;
        font-size: 15px;
        line-height: 1.65;
        color: ${MUTED};
    }
`;

const PainTitle = styled.h3`
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    text-decoration: line-through;
    text-decoration-color: rgba(217, 65, 43, 0.6);
    text-decoration-thickness: 2px;
`;

const TargetBox = styled.div`
    margin-top: 56px;
    padding: 28px;
    background: #fff;
    border: 1px dashed #cfc6b1;
    border-radius: 4px;
`;

const TargetTitle = styled.h3`
    margin: 0 0 16px;
    font-size: 20px;
    font-weight: 700;
`;

const TargetList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px 24px;

    li {
        display: flex;
        gap: 10px;
        font-size: 15px;
        font-weight: 700;
        line-height: 1.55;
    }

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

const PenCheck = styled.span`
    flex-shrink: 0;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    color: ${({ $dark }) => ($dark ? "#ff9a85" : PEN)};
`;

const BeforeAfter = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

const PaperCard = styled.div`
    padding: 28px;
    background: ${({ $highlight }) => ($highlight ? "#fff" : PAPER)};
    border: ${({ $highlight }) => ($highlight ? `2px solid ${INK}` : `1px solid ${LINE}`)};
    border-radius: 4px;
    box-shadow: ${({ $highlight }) => ($highlight ? "6px 6px 0 #e9e2d0" : "none")};
`;

const PaperLabel = styled.span`
    font-size: 12px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${({ $highlight }) => ($highlight ? PEN : MUTED)};
`;

const PaperTitle = styled.h3`
    margin: 6px 0 18px;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.024em;
`;

const PaperList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;

    li {
        padding: 10px 0;
        font-size: 15px;
        font-weight: ${({ $highlight }) => ($highlight ? 600 : 400)};
        color: ${({ $highlight }) => ($highlight ? INK : MUTED)};
        border-top: 1px solid ${LINE};
    }
`;

const StepTrack = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px;
    margin: 4px 0 18px;
`;

const StepDot = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 12px 4px;
    font-size: 13px;
    font-weight: 700;
    color: ${({ $done, $current }) => ($done || $current ? INK : "#a9a291")};
    background: ${({ $current }) => ($current ? MARKER : PAPER)};
    border-radius: 4px;

    i {
        width: 26px;
        height: 26px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-style: normal;
        font-size: 12px;
        color: ${({ $done }) => ($done ? "#fff" : INK)};
        background: ${({ $done }) => ($done ? PEN : "transparent")};
        border: 1.5px solid ${({ $done }) => ($done ? PEN : "#c9c1ad")};
        border-radius: 50%;
    }
`;

const CurriculumGrid = styled.div`
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 20px;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

const StepMenu = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;

    @media (max-width: 860px) {
        flex-direction: row;
        overflow-x: auto;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }
`;

const StepMenuItem = styled.button`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 16px 18px;
    font-size: 16px;
    font-weight: 700;
    text-align: left;
    color: ${({ $active }) => ($active ? INK : "rgba(255, 255, 255, 0.75)")};
    background: ${({ $active }) => ($active ? PAPER : "transparent")};
    border: none;
    border-left: 3px solid ${({ $active }) => ($active ? PEN : "rgba(255, 255, 255, 0.15)")};
    cursor: pointer;

    small {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: -0.01em;
        color: ${({ $active }) => ($active ? PEN : "rgba(255, 255, 255, 0.5)")};
    }

    @media (max-width: 860px) {
        flex-shrink: 0;
        padding: 12px 14px;
        font-size: 14px;
        border-left: none;
        border-bottom: 3px solid ${({ $active }) => ($active ? PEN : "rgba(255, 255, 255, 0.15)")};
    }
`;

const StepPanel = styled.div`
    padding: 32px;
    color: ${INK};
    background: ${PAPER};
    border-radius: 4px;

    small {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: -0.01em;
        color: ${PEN};
    }

    h3 {
        margin: 6px 0 8px;
        font-size: 24px;
        font-weight: 700;
        letter-spacing: -0.028em;
    }

    > p {
        margin: 0 0 20px;
        font-size: 15px;
        line-height: 1.65;
        color: ${MUTED};
    }

    ol {
        margin: 0 0 20px;
        padding: 0;
        list-style: none;
        counter-reset: point;
    }

    li {
        counter-increment: point;
        display: flex;
        gap: 14px;
        padding: 13px 0;
        font-size: 16px;
        font-weight: 700;
        border-top: 1px solid ${LINE};

        &::before {
            content: counter(point, decimal-leading-zero);
            font-weight: 700;
            color: ${PEN};
        }
    }

    @media (max-width: 480px) {
        padding: 24px 20px;
    }
`;

const StepPill = styled.span`
    display: inline-block;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 700;
    background: ${MARKER};
    border-radius: 3px;
`;

const Timetable = styled.div`
    margin-top: 48px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const TimetableHead = styled.h3`
    margin: 0;
    padding: 22px 0 10px;
    font-size: 20px;
    font-weight: 700;
`;

const TimetableRow = styled.div`
    display: grid;
    grid-template-columns: 120px 1fr;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);

    span {
        font-size: 14px;
        font-weight: 700;
        color: #ff9a85;
    }

    p {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
        color: rgba(255, 255, 255, 0.9);
    }

    @media (max-width: 480px) {
        grid-template-columns: 76px 1fr;
    }
`;

const TableScroll = styled.div`
    overflow-x: auto;
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: 4px;
`;

const CompareTable = styled.table`
    width: 100%;
    min-width: 620px;
    border-collapse: collapse;
    font-size: 15px;

    th,
    td {
        padding: 16px 20px;
        text-align: left;
        border-bottom: 1px solid ${LINE};
    }

    th {
        font-size: 13px;
        font-weight: 700;
        color: ${MUTED};
        background: ${PAPER};
    }

    th:last-child {
        color: #fff;
        background: ${INK};
    }

    td:first-child {
        font-weight: 700;
    }

    td:nth-child(2) {
        color: ${MUTED};
        text-decoration: line-through;
        text-decoration-color: rgba(217, 65, 43, 0.45);
    }

    td:last-child {
        font-weight: 700;
        background: #fffbea;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }
`;

const PlanGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-top: 28px;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

const PlanCard = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 28px 24px 24px;
    background: #fff;
    border: ${({ $primary }) => ($primary ? `2px solid ${INK}` : `1px solid ${LINE}`)};
    border-radius: 4px;
    box-shadow: ${({ $primary }) => ($primary ? "6px 6px 0 #e9e2d0" : "none")};
`;

const PlanRibbon = styled.span`
    position: absolute;
    top: 18px;
    right: -1px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    background: ${PEN};
`;

const PlanCaption = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${MUTED};
`;

const PlanName = styled.h3`
    margin: 4px 0 14px;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.024em;
`;

const PlanPrice = styled.p`
    margin: 0 0 18px;
    padding-bottom: 18px;
    font-size: 14px;
    font-weight: 700;
    color: ${MUTED};
    border-bottom: 1px solid ${LINE};

    strong {
        margin-right: 2px;
        font-size: 30px;
        font-weight: 700;
        letter-spacing: -0.03em;
        color: ${INK};
    }
`;

const PlanList = styled.ul`
    flex: 1;
    margin: 0 0 22px;
    padding: 0;
    list-style: none;

    li {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 0;
        font-size: 15px;
    }

    svg {
        color: ${PEN};
    }
`;

const FinePrint = styled.p`
    margin: 20px 0 0;
    font-size: 13px;
    text-align: center;
    color: ${MUTED};
`;

const UniRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px 18px;
    margin-bottom: 36px;

    span {
        font-size: 17px;
        font-weight: 700;
        color: #a39c8a;
    }
`;

const NoteGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 20px;

    @media (max-width: 960px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const Note = styled.figure`
    position: relative;
    margin: 0;
    padding: 30px 20px 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 18px;
    min-height: 200px;
    background: #fffbea;
    border: 1px solid #efe4bd;
    transform: rotate(${({ $tilt }) => $tilt}deg);

    &::before {
        content: "";
        position: absolute;
        top: -10px;
        left: 50%;
        width: 64px;
        height: 20px;
        background: rgba(217, 65, 43, 0.18);
        transform: translateX(-50%) rotate(-3deg);
    }

    p {
        margin: 0;
        font-size: 15px;
        line-height: 1.7;
    }

    @media (max-width: 560px) {
        transform: none;
    }
`;

const NoteOutcome = styled.figcaption`
    font-size: 13px;
    font-weight: 700;
    color: ${PEN};
`;

const ProcessRow = styled.ol`
    position: relative;
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;

    &::before {
        content: "";
        position: absolute;
        top: 20px;
        left: 12%;
        right: 12%;
        border-top: 2px dashed #cfc6b1;
    }

    @media (max-width: 860px) {
        grid-template-columns: 1fr 1fr;

        &::before {
            display: none;
        }
    }
`;

const ProcessItem = styled.li`
    position: relative;
    text-align: center;

    h3 {
        margin: 16px 0 6px;
        font-size: 17px;
        font-weight: 700;
    }

    p {
        margin: 0;
        font-size: 14px;
        line-height: 1.6;
        color: ${MUTED};
    }
`;

const ProcessNum = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    font-size: 18px;
    font-weight: 700;
    color: ${INK};
    background: ${PAPER};
    border: 2px solid ${INK};
    border-radius: 50%;
`;

const FaqList = styled.div`
    border-top: 2px solid ${INK};
`;

const FaqItem = styled.div`
    border-bottom: 1px solid ${LINE};
`;

const FaqQuestion = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 22px 4px;
    font-size: 16px;
    font-weight: 700;
    text-align: left;
    color: ${INK};
    background: none;
    border: none;
    cursor: pointer;
`;

const FaqSign = styled.span`
    margin-left: auto;
    font-size: 22px;
    font-weight: 400;
    color: ${PEN};
`;

const FaqAnswer = styled.p`
    margin: 0;
    padding: 0 4px 22px;
    font-size: 15px;
    line-height: 1.75;
    color: ${MUTED};
`;

const ClosingGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: center;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`;

const ClosingLead = styled.p`
    margin: 18px 0 26px;
    font-size: 16px;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.75);
`;

const TrustList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px 20px;

    li {
        display: flex;
        gap: 8px;
        font-size: 15px;
        font-weight: 700;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.9);
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const Floating = styled.div`
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1000;
    width: 320px;
    padding: 18px;
    background: #fff;

    @media (max-width: 1279px) {
        width: 240px;
        padding: 8px;
    }
    border: 2px solid ${INK};
    border-radius: 4px;
    box-shadow: 6px 6px 0 rgba(29, 35, 64, 0.18);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transform: translateY(${({ $visible }) => ($visible ? "0" : "16px")});
    visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
    transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;

    @media (max-width: 560px) {
        left: 0;
        right: 0;
        bottom: 0;
        width: auto;
        padding: 10px 12px;
        border: none;
        border-top: 2px solid ${INK};
        box-shadow: none;
    }
`;

const FloatingCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 14px;

    strong {
        font-size: 15px;
        font-weight: 700;
    }

    span {
        font-size: 13px;
        color: ${MUTED};
    }

    @media (max-width: 1279px) {
        display: none;
    }
`;

const FloatingActions = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 8px;
`;

const FloatingGhost = styled.button`
    padding: 15px 18px;
    font-size: 15px;
    font-weight: 700;
    color: ${INK};
    background: ${PAPER};
    border: 1px solid ${LINE};
    border-radius: 6px;
    cursor: pointer;
`;
